<script setup>
import { onMounted, ref } from 'vue';
import { useMapActions, useMapGetters, useMapState } from '@/store/useVuexCompat';
import { useWhiteLabel } from '@/store/useWhitelabel';
import Sidebar from '../components/Sidebar/Sidebar.vue';
import PageHeader from '../components/PageHeader.vue';
import PageFooter from '../components/PageFooter.vue';
import Notification from '../components/Notification.vue';

const {
    useGradient,
    reverseGradient,
    logo
} = useWhiteLabel();

// reactive state
const initialized = ref(false);
const drawer = ref(null);

// map state from auth module
const { user } = useMapState('auth', ['user']);

// map getters
const {
    authenticated
} = useMapGetters({
    authenticated: 'auth/authenticated'
});

// map actions
const { getAllSettings } = useMapActions({
    getAllSettings: 'settings/getAllSettings'
});

// lifecycle
onMounted(async () => {
    await getAllSettings();
    initialized.value = true;
});
</script>

<template>
    <v-app>
        <v-navigation-drawer
            v-model="drawer"
            clipped
            fixed
            app
            dark
            v-if="initialized && user.role !== 'builder'"
        >
            <div class="Sidebar__close-button">
                <v-btn @click="drawer = false">
                    <v-icon
                        small
                        left
                    >
                        fas fa-times-circle
                    </v-icon>
                    Close
                </v-btn>
            </div>
            <sidebar />
        </v-navigation-drawer>

        <v-app-bar
            v-if="initialized"
            app
            class="elevation-0 Header"
            :class="{'--gradient': useGradient, '--reverse-gradient': reverseGradient}"
            fixed
            dark
            clipped-left
        >
            <div
                class="PrimaryMenuTrigger"
                v-if="user.role !== 'builder'"
            >
                <v-btn
                    icon
                    @click.stop="drawer = !drawer"
                    v-if="user.role !== 'builder'"
                >
                    <i class="fas fa-bars" />
                </v-btn>
            </div>

            <div
                class="PrimaryLogo"
                :class="{jsNoTrigger:user.role === 'builder'}"
            >
                <img
                    :src="logo"
                    alt=""
                >
            </div>
            <v-spacer />
            <page-header
                app
                v-if="authenticated"
            />
        </v-app-bar>

        <v-main
            v-if="initialized"
            app
        >
            <notification />
            <transition
                name="fade"
                mode="out-in"
            >
                <router-view />
            </transition>
        </v-main>

        <page-footer />
    </v-app>
</template>

<style lang="scss">

.Header {
    display: flex;
    flex-flow: row wrap;
    align-content: center;
    align-items: center;
    flex: 0 0 100%;
    width: 100%;
}

.PrimaryLogo {
    display: flex;
    flex-flow: row nowrap;
    align-content: center;
    align-items: center;
    flex: 0 0 40px;
    width: 40px;
    margin-right: 10px;
    padding-top: 3px;

    @media (min-width: 375px) {
        flex: 0 0 60px;
        width: 60px;
    }

    @media (min-width: 500px) {
        flex: 0 0 90px;
        width: 90px;
    }

    img {
        display: inline-block;
        width: 100%;
        height: auto;
    }

    &.jsNoTrigger {
        margin-left: 12px;
    }
}

.Sidebar__close-button {
    flex: 0 0 100%;
    width: 100%;
    padding: 20px 24px 0 20px !important;

    button {
        flex: 0 0 100%;
        width: 100%;
    }

    @media (min-width: 1263px) {
        display: none !important;
    }
}

</style>
